import { integratePowerSamples, calculateDemand, calculatePeakDemand } from './energyMath';
import { demandIntervalContaining, periodContaining } from './periods';
import { coverage, withLineage } from './lineage';
import { counterEnergy } from './counterEnergyDelegate';
import * as money from './costMath';

export class RunningAccumulator {
  constructor(windowStart, windowEnd) { this.resetAccumulator(windowStart, windowEnd); }

  resetAccumulator(windowStart, windowEnd, reason = 'windowChanged') {
    Object.assign(this, { windowStart, windowEnd, lastSampleTime: null, lastPowerKw: null,
      sampleCount: 0, validSegmentSeconds: 0, positiveKwhSum: 0, negativeKwhSum: 0,
      minKw: null, maxKw: null, runningMeanKw: null, resetReason: reason, lastIdentity: null, gapCount: 0 });
  }

  update(sample) {
    const at = Date.parse(sample.sampleTime);
    if (!Number.isFinite(at) || typeof sample.kw !== 'number' || !Number.isFinite(sample.kw)) return;
    if (sample.identity && sample.identity === this.lastIdentity) return;
    if (this.lastSampleTime && at <= Date.parse(this.lastSampleTime)) return;
    if (at < Date.parse(this.windowStart) || at > Date.parse(this.windowEnd)) return;
    if (this.lastSampleTime) {
      const deltaSeconds = (at - Date.parse(this.lastSampleTime)) / 1000;
      if (deltaSeconds <= 15) {
        const segment = integratePowerSamples([{ sampleTime: this.lastSampleTime, kw: this.lastPowerKw }, sample], this.lastSampleTime, sample.sampleTime);
        this.validSegmentSeconds += segment.coveredSeconds;
        this.positiveKwhSum += segment.positiveEnergyKwh;
        this.negativeKwhSum += segment.negativeEnergyKwh;
      } else this.gapCount += 1;
    }
    this.sampleCount += 1;
    this.runningMeanKw = this.runningMeanKw == null ? sample.kw : this.runningMeanKw + (sample.kw - this.runningMeanKw) / this.sampleCount;
    this.minKw = this.minKw == null ? sample.kw : Math.min(this.minKw, sample.kw);
    this.maxKw = this.maxKw == null ? sample.kw : Math.max(this.maxKw, sample.kw);
    this.lastSampleTime = sample.sampleTime; this.lastPowerKw = sample.kw; this.lastIdentity = sample.identity;
  }

  integration() {
    const c = coverage((Date.parse(this.windowEnd) - Date.parse(this.windowStart)) / 1000, this.validSegmentSeconds);
    return { ...c, positiveEnergyKwh: this.positiveKwhSum, negativeEnergyKwh: this.negativeKwhSum,
      status: c.coveredSeconds === c.expectedSeconds ? 'complete' : c.coveredSeconds > 0 ? 'partial' : 'unavailable' };
  }
}

export class TrendAnalytics {
  constructor(configuration = {}) {
    this.configuration = configuration; this.accumulators = new Map(); this.completed = [];
    this.billingPeriodPeakKw = null; this.billingStart = null;
  }

  update(key, sample) {
    const timezone = this.configuration.timezone || 'UTC';
    const billing = periodContaining(sample.sampleTime, 'billing_period', timezone, { billingStartDay: this.configuration.billingStartDay || this.configuration.billingPeriodStartDay });
    if (billing.periodStart !== this.billingStart) { this.billingStart = billing.periodStart; this.billingPeriodPeakKw = null; }
    const completed = [];
    for (const kind of ['demand_interval', 'hour', 'day', 'billing_period']) {
      const period = kind === 'demand_interval'
        ? demandIntervalContaining(sample.sampleTime, timezone, this.configuration.demandIntervalMinutes || 15)
        : periodContaining(sample.sampleTime, kind, timezone, { billingStartDay: this.configuration.billingStartDay || this.configuration.billingPeriodStartDay });
      const id = `${key}:${kind}`;
      let accumulator = this.accumulators.get(id);
      if (!accumulator) { accumulator = new RunningAccumulator(period.periodStart, period.periodEnd); this.accumulators.set(id, accumulator); }
      if (accumulator.windowStart !== period.periodStart) {
        // A boundary sample belongs to both adjoining integrations. Gaps >15s
        // remain gaps; interpolation is confined to the sampled estimate.
        const prior = accumulator.lastSampleTime && { sampleTime: accumulator.lastSampleTime, kw: accumulator.lastPowerKw };
        let boundarySample = null;
        if (prior && Date.parse(sample.sampleTime) - Date.parse(prior.sampleTime) <= 15000) {
          const boundary = Date.parse(accumulator.windowEnd);
          if (Date.parse(prior.sampleTime) < boundary && boundary <= Date.parse(sample.sampleTime)) {
            const fraction = (boundary - Date.parse(prior.sampleTime)) / (Date.parse(sample.sampleTime) - Date.parse(prior.sampleTime));
            boundarySample = { sampleTime: accumulator.windowEnd, kw: prior.kw + (sample.kw - prior.kw) * fraction };
            accumulator.update(boundarySample);
          }
        }
        const demand = calculateDemand(accumulator.integration(), (Date.parse(accumulator.windowEnd) - Date.parse(accumulator.windowStart)) / 1000);
        const final = { ...demand, intervalStart: accumulator.windowStart, intervalEnd: accumulator.windowEnd, key, kind };
        completed.push(final);
        if (kind === 'demand_interval' && demand.status === 'complete' && ['load', 'pcc'].includes(key.split(':')[0])) {
          this.billingPeriodPeakKw = Math.max(this.billingPeriodPeakKw ?? -Infinity, demand.value);
        }
        accumulator.resetAccumulator(period.periodStart, period.periodEnd, 'intervalRollover');
        const seed = boundarySample || prior;
        if (seed && Date.parse(seed.sampleTime) === Date.parse(period.periodStart)) accumulator.update(seed);
      }
      accumulator.update(sample);
    }
    this.completed.push(...completed);
    // Completed intervals are summarized immediately; do not retain unbounded live history.
    if (this.completed.length > 4096) this.completed.splice(0, this.completed.length - 4096);
    return completed;
  }

  reset(reason) {
    this.accumulators.clear(); this.completed = []; this.billingPeriodPeakKw = null; this.resetReason = reason;
  }
}

export function counterDemandIntervals(observations, events, start, end, configuration, context = {}) {
  const timezone = configuration?.timezone || 'UTC';
  const minutes = configuration?.demandIntervalMinutes || 15;
  const rows = [];
  const ordered = observations.filter(row => row.sampleTime != null && Number.isFinite(Date.parse(row.sampleTime))).sort((a, b) => Date.parse(a.sampleTime) - Date.parse(b.sampleTime));
  let left = 0; let right = 0;
  let cursor = Date.parse(start);
  const firstPeriod = demandIntervalContaining(start, timezone, minutes);
  while (cursor < Date.parse(end)) {
    const period = minutes <= 60 && 60 % minutes === 0 ? {
      periodStart: new Date(cursor === Date.parse(start) ? Date.parse(firstPeriod.periodStart) : cursor).toISOString(),
      periodEnd: new Date((cursor === Date.parse(start) ? Date.parse(firstPeriod.periodStart) : cursor) + minutes * 60000).toISOString(),
    } : demandIntervalContaining(new Date(cursor).toISOString(), timezone, minutes);
    const a = Date.parse(period.periodStart); const b = Date.parse(period.periodEnd);
    if (b > Date.parse(end)) break;
    while (left < ordered.length && Date.parse(ordered[left].sampleTime) < a) left += 1;
    right = Math.max(right, left);
    while (right < ordered.length && Date.parse(ordered[right].sampleTime) <= b) right += 1;
    const energy = counterEnergy(ordered.slice(left, right), events, period.periodStart, period.periodEnd, context);
    const intervalDemandKw = energy.status === 'complete' ? Number(money.fixed(money.divide(energy.value, String((b - a) / 3600000), 12), 9)) : null;
    rows.push(withLineage({ value: intervalDemandKw, intervalDemandKw, intervalStart: period.periodStart, intervalEnd: period.periodEnd,
      unit: 'kW', cutoffTime: period.periodEnd, sourceWatermark: energy.sourceWatermark, status: energy.status, reasonCodes: energy.reasonCodes, coverage: energy.coverage, sourceKind: energy.sourceKind }, context));
    cursor = b;
  }
  return { intervals: rows, peak: withLineage(calculatePeakDemand(rows), context) };
}
